import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Ban, KeyRound } from "lucide-react";
import { usePerms } from "@/hooks/usePerms";

const STATE = { active: ["Actif", "status-active"], revoked: ["Révoqué", "status-inactive"], expired: ["Expiré", "status-pending"] };
const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");
const FILTERS = [["", "Tous"], ["active", "Actifs"], ["revoked", "Révoqués"], ["expired", "Expirés"]];

export default function AdminSecureConnect() {
  const { can } = usePerms();
  const [state, setState] = useState("");
  const [data, setData] = useState(null);

  const load = async (s = state) => {
    try { const { data: r } = await api.get(`/admin/secure-connect/devices${s ? `?state=${s}` : ""}`); setData(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(state); }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (d) => {
    const reason = window.prompt(`Révoquer ${d.device_id} (${d.entreprise_name}) ? Motif :`, "Révocation par PAG");
    if (reason === null) return;
    try { await api.post(`/admin/secure-connect/devices/${d.id}/revoke`, { reason }); toast.success("Appareil révoqué"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full" data-testid="admin-secure-connect-page">
      <PageHeader eyebrow="SecureConnect" title="Appareils & clés API des entreprises"
        sub="Vue transverse des appareils enregistrés par les entreprises dans leur Security Center. PAG peut révoquer un appareil compromis ; les secrets ne sont jamais visibles." />
      <div className="p-6 lg:p-10 space-y-6">
        {data && (
          <div className="grid md:grid-cols-3 gap-4">
            <Kpi label="Actifs" value={data.stats.active} icon={ShieldCheck} dark testid="asc-kpi-active" />
            <Kpi label="Révoqués" value={data.stats.revoked} icon={Ban} testid="asc-kpi-revoked" />
            <Kpi label="Expirés" value={data.stats.expired} icon={KeyRound} testid="asc-kpi-expired" />
          </div>
        )}
        <div className="flex gap-1" data-testid="asc-filters">
          {FILTERS.map(([k, l]) => <button key={k} onClick={() => setState(k)} data-testid={`asc-filter-${k || "all"}`} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${state === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>{l}</button>)}
        </div>
        <div className="bg-white border border-[#DEE2E6]" data-testid="asc-list">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Entreprise</div><div className="col-span-3">Appareil</div><div className="col-span-3">Portées</div><div className="col-span-2">Dernier appel</div><div className="col-span-1 text-right">Action</div>
          </div>
          {!data || data.devices.length === 0 ? <div className="p-10 text-center text-sm text-[#868E96]" data-testid="asc-empty">Aucun appareil.</div> : data.devices.map((d) => {
            const [l, cls] = STATE[d.state] || STATE.active;
            return (
              <div key={d.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#F1F3F5] last:border-b-0 text-sm" data-testid={`asc-device-${d.device_id}`}>
                <div className="col-span-3"><div className="font-semibold">{d.entreprise_name || "—"}</div><div className="font-mono text-[11px] text-[#868E96]">{d.entreprise_code}</div></div>
                <div className="col-span-3"><div className="font-semibold">{d.name} <span className={`status-pill ${cls} ml-1`}>{l}</span></div><div className="font-mono text-[11px] text-[#868E96]">{d.device_id} · {d.key_prefix}</div></div>
                <div className="col-span-3 flex flex-wrap gap-1">{d.scopes.map((s) => <span key={s} className="text-[10px] border border-[#002FA7]/30 text-[#002FA7] px-1.5 py-0.5 font-mono">{s}</span>)}</div>
                <div className="col-span-2 text-xs">{d.last_used_at ? fmt(d.last_used_at) : <span className="text-[#868E96]">Jamais</span>} <div className="text-[10px] text-[#868E96]">{d.usage_count} appel(s)</div></div>
                <div className="col-span-1 flex justify-end">{d.state === "active" && can("securite", "administrer") && <Button size="sm" variant="outline" onClick={() => revoke(d)} data-testid={`asc-revoke-${d.device_id}`} className="h-8 px-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Ban className="h-3.5 w-3.5" /></Button>}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, dark, testid }) {
  return (
    <div className={`p-5 border ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6]"}`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold ${dark ? "text-[#FFD700]" : "text-[#868E96]"}`}><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-display text-2xl font-black mt-2">{value}</div>
    </div>
  );
}
